import React, { useState } from "react";
import { Banner, EmptyState, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import {
  usePackageActivities,
  usePackageActivityCheckStatus,
} from "#hooks/usePackageActivities";
import { ActivityCard } from "./components/ActivityCard";
import * as styles from "./styles_vanilla.css";
import type { ActivityCheckStatus } from "#hooks/usePackageActivities";

export type ActivityTabProps = {
  /** Package whose activities to list, or null while the slideout is closed. */
  packageId: string | null;
  /** Arena both reads are scoped to; undefined until the host app resolves it,
   *  which holds them back. */
  arenaId: number | undefined;
};

const Tally: React.FC<{ label: string; count: number; countClassName?: string }> = ({
  label,
  count,
  countClassName,
}) => (
  <div className={styles.statusTally}>
    <Text variant="strong-md" asSpan className={countClassName}>
      {count}
    </Text>
    <Text variant="secondary-sm" asSpan>
      {label}
    </Text>
  </div>
);

/**
 * The package-level roll-up of the activity checks. Only the counts a reviewer
 * has to act on are tinted; the group is labeled so it reads as a summary of
 * the list below rather than a second page header.
 */
const CheckStatusHeader: React.FC<{ status: ActivityCheckStatus }> = ({ status }) => (
  <div className={styles.statusHeader} role="group" aria-label="Activity check results">
    <Tally label="Checks" count={status.total} />
    <Tally label="Passed" count={status.passed} countClassName={styles.countPassed} />
    <Tally label="Failed" count={status.failed} countClassName={styles.countFailed} />
    <Tally label="Skipped" count={status.skipped} />
    <Tally label="Not evaluated" count={status.notEvaluated} />
  </div>
);

/**
 * ActivityTab — the package-detail slideout's Activity tab. Lists the
 * source-linked activities behind the package, each expandable into its review
 * notes and provenance, under a roll-up of the package's activity checks.
 *
 * Only one card is open at a time: the detail read is per-activity, so a single
 * expanded id keeps the tab to at most one detail query in flight.
 */
export const ActivityTab: React.FC<ActivityTabProps> = ({ packageId, arenaId }) => {
  // The open card is tagged with the package it belongs to, so switching
  // packages derives a collapsed list during render: a different package's
  // activities have different ids, and a carried-over expansion would either
  // open nothing or fire a detail read for a row that isn't in the new list.
  const [openCard, setOpenCard] = useState<{
    packageId: string | null;
    activityId: string;
  } | null>(null);
  const expandedId = openCard?.packageId === packageId ? openCard.activityId : null;

  const { activities, loading, error } = usePackageActivities(packageId, arenaId);
  // A failed status read only costs the header, so it is deliberately not
  // surfaced as an error over the list — the activities themselves are still
  // reviewable without the roll-up.
  const { status } = usePackageActivityCheckStatus(packageId, arenaId);

  const toggleCard = (activityId: string) =>
    setOpenCard((current) =>
      current?.packageId === packageId && current.activityId === activityId
        ? null
        : { packageId, activityId },
    );

  if (error) {
    return (
      <Banner variant="danger" title="Couldn’t load activity" description={error.message} />
    );
  }

  if (loading && activities.length === 0) {
    return (
      <Flex alignItems="center" gap={8}>
        <LoadingIndicator size="small" />
        <Text variant="secondary-md" asSpan>
          Loading activity…
        </Text>
      </Flex>
    );
  }

  if (activities.length === 0) {
    return (
      <EmptyState
        illustration="searching"
        title="No activity"
        description="No activity has been linked to this package."
      />
    );
  }

  return (
    <div className={styles.container}>
      {status && status.total > 0 && <CheckStatusHeader status={status} />}
      {activities.map((activity) => (
        <ActivityCard
          key={activity.id}
          activity={activity}
          arenaId={arenaId}
          expanded={expandedId === activity.id}
          onToggle={() => toggleCard(activity.id)}
        />
      ))}
    </div>
  );
};
